// Pair-generation API: callers SHOULD use `generateBlobColorPair(channel, rng)`
// to obtain a coordinated [a, b] pair (complementary hues etc. are computed
// jointly). `generateBlobColor(channel, role, rng)` is the single-blob
// fallback for callers that already know both hues — it samples a fresh base
// hue independently and is NOT guaranteed to be complementary with another
// independently-generated call.

import type { BlobColor, ChannelId, Hotspot, OKLCH } from '../types.js';
import { CHANNELS, type ChannelDefinition } from './channels.js';

/** Hard cap from SPEC §11 / blob.frag uniform layout. Bumped to 24 with the
 *  N-blob rewrite (Phase 2): up to 12 blobs × ~3 hotspots typical, capped here. */
export const MAX_HOTSPOTS = 24;

const sample = (rng: () => number, [lo, hi]: [number, number]): number =>
  lo + (hi - lo) * rng();

const normHue = (h: number): number => {
  const m = h % 360;
  return m < 0 ? m + 360 : m;
};

/**
 * Pick a base hue for a channel, applying its optional warm-bias filter via
 * bounded rejection sampling.
 */
function pickBaseHue(def: ChannelDefinition, rng: () => number): number {
  let h = rng() * 360;
  if (def.hueFilter) {
    for (let i = 0; i < 32 && !def.hueFilter(h); i++) {
      h = rng() * 360;
    }
  }
  return h;
}

/**
 * Sample a hotspot UV inside the unit disk, biased away from the rim:
 *   r = sqrt(rng()) * 0.85,  θ = rng() * 2π.
 * The sqrt(rng()) factor gives uniform area distribution; the 0.85 cap
 * keeps hotspots clear of the blob silhouette.
 */
function sampleHotspotUV(rng: () => number): [number, number] {
  const r = Math.sqrt(rng()) * 0.85;
  const theta = rng() * Math.PI * 2;
  return [r * Math.cos(theta), r * Math.sin(theta)];
}

/**
 * Build hotspot list around a base color. One hotspot is the "convergence
 * highlight" with intensity ≈ 1.0; the others get 0.4..0.7.
 */
function buildHotspots(
  base: OKLCH,
  def: ChannelDefinition,
  rng: () => number,
): Hotspot[] {
  const colors = def.derive(base, rng);
  const highlightIdx = Math.floor(rng() * colors.length);

  const hotspots: Hotspot[] = colors.map((color, i) => {
    const intensity = i === highlightIdx ? 1.0 : 0.4 + rng() * 0.3;
    return {
      positionUV: sampleHotspotUV(rng),
      color,
      intensity,
    };
  });

  return hotspots;
}

/**
 * Compute the base OKLCH for a single role given a target base hue.
 * Lightness/chroma come from the channel's range (Mono uses its split).
 */
function buildBase(
  def: ChannelDefinition,
  role: 'a' | 'b',
  hue: number,
  rng: () => number,
): OKLCH {
  let lRange = def.lRange;
  if (def.monoLightnessSplit) {
    lRange = role === 'a' ? def.monoLightnessSplit.aRange : def.monoLightnessSplit.bRange;
  }
  return {
    l: sample(rng, lRange),
    c: sample(rng, def.cRange),
    h: normHue(hue),
  };
}

/**
 * Single-blob color generator. Samples a fresh base hue from the channel
 * rules — for complementary channels (ch1/ch2/ch3) this is the role-A path;
 * role B picks an *independent* hue. If you need a coordinated complementary
 * pair, prefer `generateBlobColorPair`.
 */
export function generateBlobColor(
  channel: ChannelId,
  role: 'a' | 'b',
  rng: () => number,
): BlobColor {
  const def = CHANNELS[channel];
  const baseHue = pickBaseHue(def, rng);
  const base = buildBase(def, role, baseHue, rng);
  return { base, hotspots: buildHotspots(base, def, rng) };
}

/**
 * Coordinated pair generator — the recommended entry point.
 *
 * - ch1/ch2: complementary hues with the spec'd offset jitter.
 * - ch3:     warm-biased base, then complementary partner.
 * - mono:    same hue for both (irrelevant at C ≈ 0); per-role L split keeps
 *            the two blobs distinct (gap ≥ 0.10 by construction of ranges
 *            [0.65,0.80] vs [0.40,0.55]).
 *
 * Phase 3 — `hueOverride` lets the UI's hue picker pin the base hues
 * while keeping every other quantity (L, C, jitter, hotspot offsets)
 * deterministic from `(seed, channel)`. We always consume the same
 * sequence of `rng()` calls regardless of override, so hotspot L/C and
 * per-hotspot UV positions are invariant across picker movements — only
 * the base hue (and, via the derive jitter offset, hotspot hues) shift.
 */
export interface BlobColorPairResult {
  pair: [BlobColor, BlobColor];
  /** Seed-derived hues (before any override). The UI uses these to
   *  prefill the hue pickers when a channel switch resets them. */
  seedHues: { hueA: number; hueB: number };
}

export function generateBlobColorPair(
  channel: ChannelId,
  rng: () => number,
  hueOverride?: { hueA: number; hueB: number },
): BlobColorPairResult {
  const def = CHANNELS[channel];

  // Always consume rng for the seed-derived hue, even when overridden,
  // so the downstream L/C/jitter sequence is invariant.
  const seedHueA = pickBaseHue(def, rng);
  let seedHueB: number;

  if (channel === 'mono') {
    // Hue is irrelevant at C ≈ 0; reuse to avoid spurious variation.
    seedHueB = seedHueA;
  } else {
    // ch1/ch2/ch3: complementary with jitter. Always consume the rng
    // call for the offset jitter, even if hueB is about to be
    // overridden.
    const offsetJitter = (rng() * 2 - 1) * def.hueOffsetJitterDeg;
    seedHueB = seedHueA + def.hueOffsetDeg + offsetJitter;
  }

  const baseHueA = hueOverride ? hueOverride.hueA : seedHueA;
  const baseHueB = hueOverride ? hueOverride.hueB : seedHueB;

  const baseA = buildBase(def, 'a', baseHueA, rng);
  const baseB = buildBase(def, 'b', baseHueB, rng);

  return {
    pair: [
      { base: baseA, hotspots: buildHotspots(baseA, def, rng) },
      { base: baseB, hotspots: buildHotspots(baseB, def, rng) },
    ],
    seedHues: { hueA: seedHueA, hueB: seedHueB },
  };
}

// ─── shader serialization ───────────────────────────────────────────────────

export interface ShaderColorPayload {
  /** 2 floats (UV) per hotspot, padded to MAX_HOTSPOTS. */
  positions: Float32Array;
  /** 3 floats per hotspot in OKLCH order: L, C, H (radians). Padded. */
  colors: Float32Array;
  /** 1 float per hotspot, padded. Active count is implicit via uActiveHotspots. */
  intensities: Float32Array;
  /**
   * Per-hotspot owning blob id (0.0 or 1.0). Tells the shader which blob's
   * local frame to evaluate the hotspot in — critical during the merge
   * transition when both A and B contribute hotspots in the same payload.
   * Stored as Float32Array because integer arrays are flaky across drivers.
   */
  blobIds: Float32Array;
  /** Number of populated hotspots (0..MAX_HOTSPOTS). */
  activeCount: number;
}

interface BlobColorWithId {
  color: BlobColor;
  /** Blob index in the world's blob list (0..MAX_BLOBS-1). */
  blobId: number;
}

/**
 * Pack a BlobColor's hotspots into uniform-ready Float32Arrays.
 *
 * Accepts either a single BlobColor (legacy single-blob case → all blobIds=0)
 * OR an array of {color, blobId} pairs to merge multiple blobs' hotspots into
 * one payload (used during the merge transition where the renderer needs to
 * dispatch hotspots from BOTH blobs in their respective local frames).
 *
 * - `colors` carries OKLCH (L, C, H-in-radians) — the shader does the
 *   OKLCH → OKLab → linear RGB → sRGB chain itself per SPEC §11.
 * - Arrays are always sized to MAX_HOTSPOTS (8); the shader uses
 *   `uActiveHotspots` to bound iteration.
 * - When the combined hotspot count exceeds MAX_HOTSPOTS, blob A's hotspots
 *   take priority (they are written first); blob B's tail is dropped.
 */
export function serializeForShader(
  source: BlobColor | BlobColorWithId[],
): ShaderColorPayload {
  const positions = new Float32Array(MAX_HOTSPOTS * 2);
  const colors = new Float32Array(MAX_HOTSPOTS * 3);
  const intensities = new Float32Array(MAX_HOTSPOTS);
  const blobIds = new Float32Array(MAX_HOTSPOTS);

  // Normalize to BlobColorWithId[] for a single code path.
  const sources: BlobColorWithId[] = Array.isArray(source)
    ? source
    : [{ color: source, blobId: 0 }];

  let writeIdx = 0;
  for (const { color, blobId } of sources) {
    const n = color.hotspots.length;
    for (let i = 0; i < n && writeIdx < MAX_HOTSPOTS; i++) {
      const h = color.hotspots[i]!;
      positions[writeIdx * 2] = h.positionUV[0];
      positions[writeIdx * 2 + 1] = h.positionUV[1];
      colors[writeIdx * 3] = h.color.l;
      colors[writeIdx * 3 + 1] = h.color.c;
      colors[writeIdx * 3 + 2] = (h.color.h * Math.PI) / 180; // degrees → radians
      intensities[writeIdx] = h.intensity;
      blobIds[writeIdx] = blobId;
      writeIdx++;
    }
    if (writeIdx >= MAX_HOTSPOTS) break;
  }

  return { positions, colors, intensities, blobIds, activeCount: writeIdx };
}
